// Candidate > Onboarding. Staff side of the onboarding portal. Every control is
// backed by the migration's allowlisted RPCs (see lib/onboarding.js). A login
// that is not on the staff allowlist sees the tab with every control disabled.
//
// "Ready for ADP" is behind a second click, calls onboarding_adp_view each time
// it opens (each open is a logged decrypt), and holds the values only while the
// panel is open: closing it, "Entered in ADP", switching candidate or leaving
// the tab drops them. Nothing is cached.
import React, { useCallback, useEffect, useState } from 'react';
import {
  PAGE_ORDER, PAGE_LABELS, DOC_LABELS, HEALTH_KINDS, RESTRICTED, effectiveStatus, daysLeft, isRed, NO_MIDDLE_NAME_TEXT, DEGREE_MISSING_TEXT,
  ERASE_TEXT, PACKET_KEEP_TEXT,
  onboardLink, buildOnboardingEmail, firstNameOf, stillNeededFor,
} from './lib/onboarding';
import { draftState, STATE_LABELS } from './lib/drafts';
import { FORMS } from './lib/packet';
import { makePacket, saveBytes } from './lib/packetDownload';
import { ExamsSection, SendExamsButton } from './SendExams';
import SendLink from './SendLink';

const box = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 14 };
const h = { fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '0 0 10px' };
const btn = (kind = 'primary', disabled = false) => ({
  border: 0, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  ...(kind === 'primary' ? { background: '#1d4ed8', color: '#fff' }
    : kind === 'danger' ? { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }
      : { background: '#f1f5f9', color: '#475569' }),
});
const muted = { fontSize: 12, color: '#64748b' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

const W4_LABELS = {
  filing_status: 'Filing status', multiple_jobs: 'Multiple jobs / spouse works', dependents_under_17: 'Children under 17',
  other_dependents: 'Other dependents', other_income: 'Other income', deductions: 'Deductions', extra_withholding: 'Extra withholding',
  exempt: 'Exempt',
};
// hub 2026-10-08: the reminder pt-agents drafts 48 h after the link, at most one per 72 h, into the Drafts tab.
export function lastNudgeText(n, nowMs = Date.now()) {
  if (n === undefined) return 'Last nudge: could not be read.';
  if (!n) return 'Last nudge: none yet.';
  const state = draftState(n, nowMs);
  const when = state === 'sent' && n.sent_at ? `sent ${fmt(n.sent_at)}` : `drafted ${fmt(n.created_at)}`;
  return `Last nudge: ${when} · ${STATE_LABELS[state]}`;
}
// PORTAL-PACKET-2026-10-09 §7: what the candidate entered, by section. Values only while the panel is open.
const yn = (v) => (v === true || v === 'yes' ? 'Yes' : v === false || v === 'no' ? 'No' : v);
export function detailSections(d) {
  const p = d.personal || {};
  const bg = d.background || {};
  const monthYear = (v) => (/^\d{4}-\d{2}$/.test(String(v || '')) ? `${String(v).slice(5)}/${String(v).slice(0, 4)}` : v);
  const e = d.emergency || {};
  const t = d.taxHome || {};
  const contact = (n) => [
    [`Contact ${n}`, e[`c${n}_name`]], ['Relationship', e[`c${n}_relationship`]], ['Mobile', e[`c${n}_mobile`]],
    ['Other phone', e[`c${n}_alt_phone`]], ['Home phone', e[`c${n}_home_phone`]], ['Work phone', e[`c${n}_work_phone`]],
    ['Address', e[`c${n}_address`]], ['City', e[`c${n}_city`]], ['State', e[`c${n}_state`]], ['ZIP', e[`c${n}_zip`]],
  ];
  return [
    ['About you', [['Legal name', p.legal_name], ['Date of birth', p.dob], ['Mobile', p.mobile], ['Home phone', p.home_phone],
      ['Work phone', p.work_phone], ['Email', p.email], ['Other names', p.other_names], ['Discipline', p.discipline],
      ['Address', [p.address_line1, p.address_line2].filter(Boolean).join(', ')], ['City', p.city], ['State', p.state], ['ZIP', p.zip],
      ['Previous address (past 7 years)', [p.prev1_address, p.prev1_city, p.prev1_state, p.prev1_zip].filter(Boolean).join(', ')],
      ['Another previous address', [p.prev2_address, p.prev2_city, p.prev2_state, p.prev2_zip].filter(Boolean).join(', ')]]],
    ['Emergency contacts', [...contact(1), ...contact(2), ['Allergies / special health considerations', e.allergies]]],
    ['Tax home', [['Facility', t.facility], ['Start date', t.start_date], ['End date', t.end_date], ['Tax home address', t.address],
      ['Permanent address', [t.perm_address_line1, t.perm_address_line2, t.perm_city, t.perm_state, t.perm_zip].filter(Boolean).join(', ')],
      ['How long lived there', t.tenure], ['Monthly cost of the tax home (USD)', t.monthly_expense],
      ['Works near the primary residence and lives there on this assignment', yn(t.form_q_vicinity)],
      ['Pays expenses to support the primary residence', yn(t.form_q_expenses)], ['Will return to the primary residence', yn(t.form_q_return)],
      ['Foreign student (F-1, M-1, J-1)', yn(t.form_q_foreign_student)],
      ['IRS 463 Q1', yn(t.q1)], ['IRS 463 Q2', yn(t.q2)], ['IRS 463 Q3', yn(t.q3)], ['IRS 463 Q4', yn(t.q4)], ['IRS 463 Q5', yn(t.q5)],
      ['Certified true and complete', yn(t.certified)]]],
    // PORTAL-DEGREE-2026-10-12: the background record's degree details, for the education verification
    ['Background check: degree', [['Degree obtained', bg.degree], ['Major or field of study', bg.degree_major],
      ['Month and year graduated', monthYear(bg.degree_graduated)], ['Name of school', bg.degree_school],
      ['City and state of school', bg.degree_school_city_state], ['Last name used at time of graduation', bg.degree_last_name]]],
  ].map(([title, rows]) => [title, rows.filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')]);
}

const show = (v) => (v === true ? 'Yes' : v === false ? 'No' : v === null || v === undefined || v === '' ? '—' : String(v));

// CRM-ONBOARDING-TAB-2026-10-13: the actions that change what the Onboarding tab's list shows (it reloads on each).
const CHANGES_LIST = /^(start|revoke|extend|sterling|entered|packet)/;

export default function OnboardingTab({ candidate: c, api, toast = () => {}, config, gmailSignedIn = false, createDraft, staffEmail,
  packetFn = makePacket, saveFn = saveBytes, onChanged = () => {}, examsApi = null, sendApi = null }) {
  // ONBOARD-EXAMS-v2-2026-10-16: "Send exams" beside the link -- a separate send; the Exams box below has the modal
  const [examsOpen, setExamsOpen] = useState(false);
  const [examsReady, setExamsReady] = useState(false);   // the exams migration is applied (the box below says)
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [freshLink, setFreshLink] = useState(null);   // shown once, never re-fetched
  const [adpArmed, setAdpArmed] = useState(false);
  const [relinkArmed, setRelinkArmed] = useState(false);   // ONBOARD-RELINK-2026-10-15: a second click before the old link dies
  const [adp, setAdp] = useState(null);               // decrypted values: only while the panel is open
  const [details, setDetails] = useState(null);       // what they entered: only while the panel is open (logged on open)
  const [packetNote, setPacketNote] = useState('');
  // §4 (OVERNIGHT-DIRECTSTAFF-2026-10-10): no full packet download on file -> "Entered in ADP" asks for a second click.
  const [adpGuard, setAdpGuard] = useState(null);     // null | { unread: boolean }

  const reload = useCallback(async () => {
    setError('');
    try { setData(await api.load(c.id)); } catch (e) { setError(e.message); }
  }, [api, c.id]);

  useEffect(() => { setData(null); setFreshLink(null); setAdp(null); setAdpArmed(false); setRelinkArmed(false); setDetails(null); setPacketNote(''); setAdpGuard(null); reload(); }, [c.id, reload]);
  useEffect(() => () => { setAdp(null); setDetails(null); }, []); // leaving the tab drops any shown values

  const allowed = !!(data && data.allowed);
  const off = !allowed || !!busy;
  const latest = allowed ? data.latest : null;
  const status = effectiveStatus(latest);
  const open = ['pending', 'in_progress'].includes(status);
  const extendable = !!latest && ['pending', 'in_progress'].includes(latest.status);   // past its expiry too
  const expired = extendable && new Date(latest.expires_at).getTime() < Date.now();
  const relinkable = extendable && !expired;
  const signedForms = allowed ? data.signedForms : undefined;
  const sterling = allowed ? data.sterling : undefined;
  const still = allowed ? stillNeededFor(data) : undefined;

  // DAY-DIRECTSTAFF-2026-10-22: what this candidate's facility requires -- both ticks saved together, then re-read
  const setHealthTick = (kind, on) => run('health', async () => {
    const next = { ...data.health, [kind]: on };
    await api.setHealth(c.id, next.tb_test === true, next.physical === true);
    await reload();
  });

  const run = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      if (CHANGES_LIST.test(label)) onChanged();
    } catch (e) { toast(e.message); setError(e.message); } finally { setBusy(''); }
  };

  const start = () => run('start', async () => {
    const r = await api.start(c.id);
    setFreshLink({ link: onboardLink(r.token, config.baseUrl), expiresAt: r.expires_at, sessionId: r.session_id });
    await reload();
  });
  const revoke = () => run('revoke', async () => { await api.revoke(latest.id); setFreshLink(null); await reload(); });
  // CRM-ONBOARDING-TAB-2026-10-13: seven more days on an open link (even one past its expiry); the link itself is unchanged
  const extend = () => run('extend', async () => {
    const until = await api.extendLink(latest.id);
    toast(`Link extended to ${fmt(until)}`);
    await reload();
  });
  // ONBOARD-RELINK-2026-10-15: a new token for the SAME session -- every answer, signature, consent and upload stays; the
  // previous link stops working. Shown once in the same box as a new link.
  const RELINK_SAYS = {
    'onboarding:not_open': 'This link is revoked or finished, so it can\'t be re-issued. Start a new link instead.',
    'onboarding:extend_first': 'This link has expired. Extend it first, then issue the new link.',
  };
  const relink = () => run('relink', async () => {
    let r;
    try { r = await api.relink(latest.id); } catch (e) { throw new Error(RELINK_SAYS[e.message] || e.message); }
    setRelinkArmed(false);
    setFreshLink({ link: onboardLink(r.token, config.baseUrl), expiresAt: r.expires_at, kept: true, sessionId: r.session_id });
    toast('New link issued -- progress kept');
    await reload();
  });
  const markSterling = () => run('sterling', async () => { await api.markSentToSterling(latest.id); toast('Marked sent to Sterling'); await reload(); });
  const copy = async () => {
    try { await navigator.clipboard.writeText(freshLink.link); toast('Link copied'); } catch { toast('Copy failed — select the link and copy it'); }
  };
  const draft = () => run('draft', async () => {
    const { subject, body } = buildOnboardingEmail({ firstName: firstNameOf(c.name), link: freshLink.link, expiresAt: freshLink.expiresAt, config });
    await createDraft(c.email, subject, body);
    toast('Draft created in Gmail — review it there before sending');
  });
  const openAdp = () => run('adp', async () => { setAdp(await api.adpView(c.id)); setAdpArmed(false); await reload(); });
  const closeAdp = () => { setAdp(null); setAdpArmed(false); setAdpGuard(null); };
  // Hub ruling 2026-10-14: the numbers first (the database), then the voided check image(s) (storage). A failed image
  // delete is shown, and the button below offers it again; the numbers stay erased either way.
  // -> '' or the failure's sentence, shown AFTER the reload (a reload clears the error line)
  const deleteVoided = async () => {
    try { const n = await api.deleteVoidedChecks(c.id); if (n) toast(`Voided check image deleted (${n})`); return ''; }
    catch (e) { toast(e.message); return e.message; }
  };
  const erase = async () => {
    await api.markEnteredInAdp(c.id); setAdpGuard(null); setAdp(null); setAdpArmed(false);
    const failed = await deleteVoided();
    await reload();
    if (failed) setError(failed);
  };
  const retryVoided = () => run('voided', async () => { const failed = await deleteVoided(); await reload(); if (failed) setError(failed); });
  // The first click checks the candidate's activity for a full packet download; none (or unreadable) -> the second click.
  const entered = () => run('entered', async () => {
    const downloaded = await api.packetDownloaded(c.id);
    if (downloaded !== true) { setAdpGuard({ unread: downloaded === null }); return; }
    await erase();
  });
  // The second click: the override is logged first, and a failed log erases nothing.
  const enteredAnyway = () => run('entered', async () => { await api.logAdpOverride(c.id, staffEmail, adpGuard.unread); await erase(); });
  const openDetails = () => run('details', async () => { setDetails(await api.details(c.id, staffEmail)); });
  const packet = (only = null) => run(only ? `packet:${only}` : 'packet', async () => {
    setPacketNote('');
    const out = await packetFn({ api, candidate: c, by: staffEmail, only });
    saveFn(out.bytes, out.filename);
    if (!only) setAdpGuard(null);                    // a full download is now on file
    setPacketNote(`${only ? FORMS.find((f) => f.key === only).label : 'Packet'} downloaded: ${out.pages} pages.`
      + (out.skipped && out.skipped.length ? ` Not embedded (an older file type): ${out.skipped.join(', ')}.` : '')
      + (out.cursive === false ? ' Signatures are in plain italics: the script font could not be loaded.' : ''));
  });
  const openDoc = (path) => run('doc', async () => { window.open(await api.signedUrl(path), '_blank', 'noopener'); });
  const addFacilityDoc = (assignment, file) => run('facility', async () => {
    await api.addFacilityDocument(assignment, c.id, file, staffEmail); await reload(); toast('Facility document added');
  });

  const emailBlocked = !gmailSignedIn ? 'Connect Gmail to create the draft.'
    : (config.missing && config.missing.length) ? `Email needs ${config.missing.join(', ')} in the build environment.` : '';

  return (
    <div data-testid="onboarding-tab">
      {!allowed && data && <p data-testid="onb-restricted" style={{ ...box, color: '#92400e', background: '#fffbeb', borderColor: '#fde68a' }}>{RESTRICTED}</p>}
      {error && allowed && <p data-testid="onb-error" style={{ color: '#b91c1c', fontSize: 13 }}>{error}</p>}

      <section style={box}>
        <h3 style={h}>Link</h3>
        <p style={muted} data-testid="onb-status">
          {latest ? `Status: ${status.replace('_', ' ')} · created ${fmt(latest.created_at)} · expires ${fmt(latest.expires_at)}` : 'No onboarding link yet.'}
        </p>
        {allowed && <p style={muted} data-testid="onb-last-nudge">{lastNudgeText(data.lastNudge)}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <button data-testid="onb-start" style={btn('primary', off)} disabled={off} onClick={start}>
            {open ? 'Start over with a new link' : 'Start onboarding'}
          </button>
          <button data-testid="onb-extend" style={btn('ghost', off || !extendable)} disabled={off || !extendable} onClick={extend}>Extend link 7 days</button>
          {!relinkArmed && (
            <button data-testid="onb-relink" style={btn('ghost', off || !relinkable)} disabled={off || !relinkable} onClick={() => setRelinkArmed(true)}>New link, keep progress</button>
          )}
          <button data-testid="onb-revoke" style={btn('danger', off || !open)} disabled={off || !open} onClick={revoke}>Revoke link</button>
          {examsApi && <SendExamsButton compact ready={allowed && examsReady} onOpen={() => setExamsOpen(true)} />}
        </div>
        {relinkArmed && (
          <div data-testid="onb-relink-confirm-box" style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={muted}>The current link stops working at once. Everything the candidate has done stays.</span>
            <button data-testid="onb-relink-confirm" style={btn('primary', off)} disabled={off} onClick={relink}>Issue the new link</button>
            <button data-testid="onb-relink-cancel" style={btn('ghost')} onClick={() => setRelinkArmed(false)}>Cancel</button>
          </div>
        )}
        {allowed && expired && <p data-testid="onb-relink-expired" style={{ ...muted, marginTop: 6 }}>The link has expired: extend it before issuing a new one.</p>}
        {!freshLink && latest && <p style={{ ...muted, marginTop: 6 }}>The link is shown once, when it is made. To send it again, use "New link, keep progress": the old link stops working and nothing the candidate did is lost.</p>}
        {/* EXAMS-SEND-2026-10-16: one Send dialog -- copy, text, email (the company address, not a Gmail draft) */}
        {freshLink && sendApi && (
          <div data-testid="onb-fresh">
            {freshLink.kept && <p data-testid="onb-fresh-kept" style={{ ...muted, color: '#15803d', margin: '12px 0 0' }}>Same checklist, all progress kept. The previous link no longer works.</p>}
            <SendLink key={freshLink.link} kind="onboarding" link={freshLink.link} linkRef={freshLink.sessionId || (latest && latest.id)} candidate={c} api={sendApi} toast={toast} onSent={() => onChanged()} />
            <button data-testid="onb-hide" style={{ ...btn('ghost'), marginTop: 8 }} onClick={() => setFreshLink(null)}>Done</button>
          </div>
        )}
        {freshLink && !sendApi && (
          <div data-testid="onb-fresh" style={{ marginTop: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12 }}>
            <p style={{ ...muted, color: '#1d4ed8', margin: '0 0 6px' }}>Shown once. Copy it now; it is not shown again.</p>
            {freshLink.kept && <p data-testid="onb-fresh-kept" style={{ ...muted, color: '#15803d', margin: '0 0 6px' }}>Same checklist, all progress kept. The previous link no longer works.</p>}
            <code data-testid="onb-link" style={{ wordBreak: 'break-all', fontSize: 13 }}>{freshLink.link}</code>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button data-testid="onb-copy" style={btn('ghost')} onClick={copy}>Copy link</button>
              <button data-testid="onb-draft" style={btn('ghost', !!emailBlocked || !!busy)} disabled={!!emailBlocked || !!busy} onClick={draft}>Create Gmail draft</button>
              <button data-testid="onb-hide" style={btn('ghost')} onClick={() => setFreshLink(null)}>Done</button>
            </div>
            {emailBlocked && <p data-testid="onb-draft-blocked" style={{ ...muted, marginTop: 6 }}>{emailBlocked}</p>}
          </div>
        )}
        {allowed && data.sessions && data.sessions.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <p style={{ ...muted, fontWeight: 700, margin: '0 0 4px' }}>Link history</p>
            <ul data-testid="onb-history" style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12, color: '#475569' }}>
              {data.sessions.map((s) => (
                <li key={s.id} data-testid={`onb-history-${s.id}`} style={{ padding: '3px 0', borderBottom: '1px solid #f1f5f9' }}>
                  Sent {fmt(s.created_at)} · {effectiveStatus(s).replace('_', ' ')}
                  {s.completed_at ? ` · finished ${fmt(s.completed_at)}` : ''}
                  {s.revoked_at ? ` · revoked ${fmt(s.revoked_at)}` : ` · expires ${fmt(s.expires_at)}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {examsApi && allowed && <ExamsSection candidate={c} api={examsApi} config={config} toast={toast} open={examsOpen} setOpen={setExamsOpen} onReady={setExamsReady} sendApi={sendApi} />}

      {/* 2026-10-14 (candidate 78, session 8): the same "still needed" list the candidate sees, item by item */}
      {allowed && latest && (
        <section style={{ ...box, background: still && still.length ? '#fffbeb' : '#fff', borderColor: still && still.length ? '#fde68a' : '#e2e8f0' }} data-testid="onb-still">
          <h3 style={h}>Still needed</h3>
          {still === null && <p style={muted}>Could not be read.</p>}
          {still && still.length === 0 && <p style={{ ...muted, color: '#15803d' }} data-testid="onb-still-none">Nothing: every form is signed and every page confirmed.</p>}
          {still && still.length > 0 && (
            <ul data-testid="onb-still-list" style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#92400e' }}>
              {still.map((t) => <li key={t}>{t}</li>)}
            </ul>
          )}
        </section>
      )}

      <section style={box}>
        <h3 style={h}>Checklist</h3>
        {allowed && data.legalName && <p style={muted} data-testid="onb-legal-name">Legal name (read-only): <b style={{ color: '#0f172a' }}>{data.legalName}</b></p>}
        <ul data-testid="onb-pages" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {PAGE_ORDER.map((p) => {
            const row = allowed && data.pages.find((x) => x.page === p);
            return (
              <li key={p} data-testid={`onb-page-${p}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                <span>{PAGE_LABELS[p]}
                  {/* hub 2026-10-12: the background authorization was signed with the "I have no middle name" tick */}
                  {p === 'background' && allowed && data.noMiddleName === true && (
                    <span data-testid="onb-no-middle-name" style={{ marginLeft: 8, fontSize: 12, color: '#475569', background: '#f1f5f9', borderRadius: 6, padding: '1px 6px' }}>
                      {NO_MIDDLE_NAME_TEXT}</span>
                  )}
                  {/* PORTAL-DEGREE-2026-10-12: signed before the degree details were asked -- staff ask the candidate */}
                  {p === 'background' && allowed && data.degreeMissing === true && (
                    <span data-testid="onb-degree-missing" style={{ marginLeft: 8, fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '1px 6px' }}>
                      {DEGREE_MISSING_TEXT}</span>
                  )}
                </span>
                <span style={{ color: row && row.attested_at ? '#15803d' : '#64748b' }}>
                  {row && row.attested_at ? `Confirmed ${fmt(row.attested_at)}` : row ? 'Started' : 'Not started'}
                </span>
              </li>
            );
          })}
        </ul>
        {latest && latest.completed_at && <p style={{ ...muted, color: '#15803d', marginTop: 8 }} data-testid="onb-complete">Completed {fmt(latest.completed_at)}</p>}
      </section>

      {/* CRM-ONBOARDING-TAB-2026-10-13: the six signed forms, each viewable on its own (the packet's one-form download) */}
      <section style={box}>
        <h3 style={h}>Signed forms</h3>
        <ul data-testid="onb-signed-forms" style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
          {FORMS.map((f) => {
            const at = signedForms ? signedForms[f.key] : undefined;
            return (
              <li key={f.key} data-testid={`onb-signed-${f.key}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span>{f.label}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: at ? '#15803d' : '#64748b' }}>
                    {!allowed || signedForms === undefined ? '' : at ? `Signed ${fmt(at)}` : 'Not signed'}</span>
                  <button data-testid={`onb-packet-${f.key}`} style={btn('ghost', off || !latest)} disabled={off || !latest} onClick={() => packet(f.key)}>View</button>
                </span>
              </li>
            );
          })}
        </ul>
        <p style={{ ...muted, marginTop: 6 }}>View downloads that one form, filled and signed, and is logged like the packet.</p>
      </section>

      <section style={box} data-testid="onb-details-box">
        <h3 style={h}>What they entered</h3>
        {!details && (
          <button data-testid="onb-details-open" style={btn('ghost', off || !latest)} disabled={off || !latest} onClick={openDetails}>Show details…</button>
        )}
        {!details && <p style={{ ...muted, marginTop: 6 }}>Opening it is logged on the candidate's activity.</p>}
        {details && (
          <div data-testid="onb-details">
            {detailSections(details).map(([title, rows]) => (
              <div key={title} style={{ marginBottom: 10 }}>
                <p style={{ ...muted, fontWeight: 700, margin: '0 0 4px' }}>{title}</p>
                {rows.length === 0 ? <p style={muted}>Nothing entered yet.</p> : (
                  <table style={{ fontSize: 13, borderCollapse: 'collapse' }}><tbody>
                    {rows.map(([k, v]) => <tr key={k}><td style={{ paddingRight: 12, color: '#475569' }}>{k}</td><td>{String(v)}</td></tr>)}
                  </tbody></table>
                )}
              </div>
            ))}
            <button data-testid="onb-details-close" style={btn('ghost')} onClick={() => setDetails(null)}>Hide</button>
          </div>
        )}
      </section>

      <section style={box} data-testid="onb-packet-box">
        <h3 style={h}>Packet</h3>
        <p style={muted}>The six forms filled from the candidate's answers, then the voided check, ID photos and I-20. Built here, never stored. Each download is logged; it reads the SSN and bank numbers the same way "Ready for ADP" does.</p>
        <p data-testid="onb-packet-warn" style={{ ...muted, color: '#92400e' }}>Download it before "Entered in ADP": that erases {ERASE_TEXT}, and the forms then print without them.</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <button data-testid="onb-packet" style={btn('primary', off || !latest)} disabled={off || !latest} onClick={() => packet()}>Download packet</button>
        </div>
        {packetNote && <p data-testid="onb-packet-note" style={{ ...muted, marginTop: 6 }}>{packetNote}</p>}
        {/* hub ruling 2026-10-14: after any download */}
        {packetNote && <p data-testid="onb-packet-keep" style={{ ...muted, marginTop: 4, color: '#0f172a', fontWeight: 600 }}>{PACKET_KEEP_TEXT}</p>}
      </section>

      {/* CRM-ONBOARDING-TAB-2026-10-13: ProTherapy forwards the signed background authorization to Sterling; this records it */}
      <section style={box} data-testid="onb-sterling-box">
        <h3 style={h}>Sterling</h3>
        {allowed && sterling && sterling.at ? (
          <p data-testid="onb-sterling-sent" style={{ ...muted, color: '#15803d' }}>Sent to Sterling {fmt(sterling.at)} by {sterling.by}</p>
        ) : (
          <>
            <button data-testid="onb-sterling-mark" style={btn('primary', off || !(signedForms && signedForms.background))}
              disabled={off || !(signedForms && signedForms.background)} onClick={markSterling}>Mark sent to Sterling</button>
            {allowed && signedForms && !signedForms.background && <p style={{ ...muted, marginTop: 6 }}>The background authorization is not signed yet.</p>}
          </>
        )}
      </section>

      <section style={box}>
        <h3 style={h}>On file</h3>
        <ul data-testid="onb-onfile" style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
          {[['ssn_on_file', 'SSN'], ['routing_on_file', 'Routing number'], ['account_on_file', 'Account number']].map(([k, label]) => (
            <li key={k} data-testid={`onb-onfile-${k}`}>{label}: {allowed ? (data.onFile[k] ? 'on file' : 'not on file') : '—'}</li>
          ))}
        </ul>
        {allowed && data.onFile.entered_in_adp_at && <p style={muted} data-testid="onb-entered">Entered in ADP {fmt(data.onFile.entered_in_adp_at)} by {data.onFile.entered_in_adp_by}</p>}
        {/* a voided check image still on file after "Entered in ADP" (its delete failed): offered again */}
        {allowed && data.onFile.entered_in_adp_at && data.documents.some((d) => d.kind === 'voided_check') && (
          <button data-testid="onb-voided-retry" style={{ ...btn('danger', !!busy), marginTop: 6 }} disabled={!!busy} onClick={retryVoided}>
            Delete the voided check image</button>
        )}

        <div style={{ marginTop: 12 }}>
          {!adp && !adpArmed && (
            <button data-testid="onb-adp-arm" style={btn('ghost', off)} disabled={off} onClick={() => setAdpArmed(true)}>Ready for ADP…</button>
          )}
          {!adp && adpArmed && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={muted}>Opening shows the SSN and bank numbers and is logged.</span>
              <button data-testid="onb-adp-open" style={btn('primary', off)} disabled={off} onClick={openAdp}>Show for ADP entry</button>
              <button data-testid="onb-adp-cancel" style={btn('ghost')} onClick={() => setAdpArmed(false)}>Cancel</button>
            </div>
          )}
          {adp && (
            <div data-testid="onb-adp-panel" style={{ border: '2px solid #1d4ed8', borderRadius: 8, padding: 12 }}>
              <p style={{ ...muted, margin: '0 0 8px' }}>Visible only while this panel is open. This view was logged.</p>
              <table style={{ fontSize: 13, borderCollapse: 'collapse' }}><tbody>
                <tr><td style={{ paddingRight: 12 }}>Legal name</td><td data-testid="adp-legal_name">{show(adp.legal_name)}</td></tr>
                {Object.entries(W4_LABELS).map(([k, label]) => (
                  <tr key={k}><td style={{ paddingRight: 12 }}>{label}</td><td>{show(adp.w4 && adp.w4[k])}</td></tr>
                ))}
                <tr><td style={{ paddingRight: 12 }}>SSN</td><td data-testid="adp-ssn" style={{ fontFamily: 'monospace' }}>{show(adp.ssn)}</td></tr>
                <tr><td style={{ paddingRight: 12 }}>Bank</td><td>{show(adp.direct_deposit && adp.direct_deposit.bank_name)} · {show(adp.direct_deposit && adp.direct_deposit.account_type)}</td></tr>
                <tr><td style={{ paddingRight: 12 }}>Routing</td><td data-testid="adp-routing" style={{ fontFamily: 'monospace' }}>{show(adp.routing)}</td></tr>
                <tr><td style={{ paddingRight: 12 }}>Account</td><td data-testid="adp-account" style={{ fontFamily: 'monospace' }}>{show(adp.account)}</td></tr>
              </tbody></table>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {!adpGuard && <button data-testid="onb-adp-entered" style={btn('primary', !!busy)} disabled={!!busy} onClick={entered}>Entered in ADP</button>}
                <button data-testid="onb-adp-close" style={btn('ghost')} onClick={closeAdp}>Close</button>
              </div>
              {adpGuard && (
                <div data-testid="onb-adp-guard" role="alert" style={{ marginTop: 10, border: '1px solid #fca5a5', background: '#fef2f2', borderRadius: 8, padding: 10 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#991b1b' }}>
                    {adpGuard.unread ? 'Whether the packet was downloaded could not be read.' : 'The packet has never been downloaded for this candidate.'}
                    {' '}"Entered in ADP" erases {ERASE_TEXT}, and the forms then print without them. Download the packet first, or confirm to erase anyway.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button data-testid="onb-adp-override" style={btn('danger', !!busy)} disabled={!!busy} onClick={enteredAnyway}>Erase without the packet</button>
                    <button data-testid="onb-adp-override-cancel" style={btn('ghost')} onClick={() => setAdpGuard(null)}>Cancel</button>
                  </div>
                </div>
              )}
              <p data-testid="onb-adp-erases" style={{ ...muted, marginTop: 6 }}>"Entered in ADP" erases {ERASE_TEXT}.</p>
            </div>
          )}
        </div>
      </section>

      <section style={box}>
        <h3 style={h}>Uploaded documents and expirations</h3>
        <ul data-testid="onb-docs" style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
          {allowed && data.documents.length === 0 && <li style={muted}>None uploaded yet.</li>}
          {allowed && data.documents.map((d) => {
            const days = daysLeft(d.expires_on);
            return (
              <li key={d.id} data-testid={`onb-doc-${d.kind}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span>{DOC_LABELS[d.kind] || d.kind}{d.license_state ? ` · ${d.license_state}` : ''}</span>
                <span>
                  {d.expires_on && <span data-testid={`onb-exp-${d.kind}`} data-red={isRed(days) ? 'yes' : 'no'}
                    style={{ color: isRed(days) ? '#b91c1c' : '#475569', fontWeight: isRed(days) ? 700 : 400, marginRight: 10 }}>
                    expires {d.expires_on} ({days} days)</span>}
                  <button data-testid={`onb-doc-view-${d.id}`} style={btn('ghost', off)} disabled={off} onClick={() => openDoc(d.storage_path)}>View</button>
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* DAY-DIRECTSTAFF-2026-10-22 (Matt's ruling): the portal's Health uploads, required only when ticked; dark (null)
          until the hub applies 2026-10-22-onboarding-health.sql */}
      {allowed && data.health && (
        <section style={box} data-testid="onb-health">
          <h3 style={h}>Health uploads the facility requires</h3>
          <p style={{ ...muted, margin: '0 0 6px' }}>Tick what this candidate's facility asks for. Only a ticked upload counts in their progress and is asked for; the portal shows the others as "if your facility requires it".</p>
          {HEALTH_KINDS.map((k) => {
            const have = data.documents.some((d) => d.kind === k);
            return (
              <label key={k} htmlFor={`onb-health-${k}`} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                <input id={`onb-health-${k}`} type="checkbox" data-testid={`onb-health-${k}`} checked={data.health[k] === true} disabled={off}
                  onChange={(e) => setHealthTick(k, e.target.checked)} style={{ width: 20, height: 20 }} />
                <span>{DOC_LABELS[k]} required</span>
                <span data-testid={`onb-health-${k}-state`} style={{ marginLeft: 'auto', fontSize: 12, color: have ? '#166534' : data.health[k] ? '#92400e' : '#475569' }}>
                  {have ? 'Uploaded' : data.health[k] ? 'Not uploaded yet' : 'Not required'}</span>
              </label>
            );
          })}
          {data.health.set_by && <p data-testid="onb-health-set" style={{ ...muted, margin: '6px 0 0' }}>Last set by {data.health.set_by} · {fmt(data.health.set_at)}</p>}
        </section>
      )}
      {allowed && data.health === undefined && <p data-testid="onb-health-unread" style={{ ...muted, margin: '0 0 14px' }}>Which health uploads the facility requires could not be read.</p>}

      <section style={box}>
        <h3 style={h}>Facility documents</h3>
        {allowed && data.assignments.length === 0 && <p style={muted}>No assignment yet.</p>}
        {(allowed ? data.assignments : [{ id: 0, facility: '' }]).slice(0, 1).map((a) => (
          <div key={a.id}>
            {a.facility && <p style={muted}>{a.facility}{a.start_date ? ` · ${a.start_date} – ${a.end_date || ''}` : ''}</p>}
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px', fontSize: 13 }}>
              {(a.facility_documents || []).map((f) => (
                <li key={f.path}>{f.name} <button style={btn('ghost', off)} disabled={off} onClick={() => openDoc(f.path)}>Open</button></li>
              ))}
            </ul>
            <input data-testid="onb-facility-upload" type="file" disabled={off || !a.id}
              onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) addFacilityDoc(a, f); }} />
          </div>
        ))}
      </section>

      <section style={box}>
        <h3 style={h}>Decrypt log</h3>
        <ul data-testid="onb-decrypt-log" style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12, color: '#475569' }}>
          {allowed && data.decryptLog.length === 0 && <li>No decrypts.</li>}
          {allowed && data.decryptLog.map((r, i) => (
            <li key={i}>{fmt(r.at)} · {r.field} · by {String(r.caller_uid).slice(0, 8)}…</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
